import React from "react";

import { useAppType, useScroll, useTranslation, useUserContext } from "utils";

import { ArticleCard, Carousel, Col, CourseCard, ErrorBoundary, Icon, NewsCard, Row, Svg, SwimlaneControls, TopicSuggestionCard } from "components";

const carouselProps = (appType, ref, desktopSlides) => ({
  arrows: false,
  dots: false,
  draggable: !appType.desktop,
  infinite: false,
  slidesToShow: appType.desktop ? desktopSlides : 1.1,
  swipeToSlide: !appType.desktop,
  ref: ref,
  rows: 1,
  type: appType.desktop ? "large" : "mini",
});

const CourseSwimlane = ({ appType, courses, swimlane, t }) => {
  const { handleScroll, ref, scrollPosition } = useScroll(appType);

  return (
    <ErrorBoundary>
      <section className="swimlane padded-small">
        <Row className="header-row">
          {/* prelint-ignore-col */}
          {appType.desktop ? (
            <>
              <Col sm={24} md={20} lg={16} className="header-container">
                <div className="icon-container">
                  <Icon iconName={swimlane.icon} size="m" />
                </div>
                <h2>{t(swimlane.translationKey)}</h2>
              </Col>
              <SwimlaneControls handleScroll={handleScroll} items={courses.length + 1} position={scrollPosition} visibleItems={3} />
            </>
          ) : (
            <Col className="header-container">
              <div className="icon-container">
                <Icon iconName={swimlane.icon} size="m" />
              </div>
              <h2>{t(swimlane.translationKey)}</h2>
            </Col>
          )}
        </Row>
        <Row>
          <Col>
            {courses.length === 1 ? (
              appType.mobile ? (
                <Carousel {...carouselProps(appType, ref, 3)}>
                  <CourseCard course={courses[0]} />
                  <TopicSuggestionCard />
                </Carousel>
              ) : (
                <Row>
                  <Col sm={16} md={8}>
                    <CourseCard course={courses[0]} />
                  </Col>
                  <Col sm={8} md={16}>
                    <TopicSuggestionCard />
                  </Col>
                </Row>
              )
            ) : (
              <Carousel {...carouselProps(appType, ref, 3)}>
                {courses.map((course) => (
                  <div key={course.slug}>
                    <CourseCard course={course} />
                  </div>
                ))}
              </Carousel>
            )}
          </Col>
        </Row>
      </section>
    </ErrorBoundary>
  );
};

const GoalSwimlane = ({ appType, filterDairyArticles, swimlane, t }) => {
  const { handleScroll, ref, scrollPosition } = useScroll();

  const recommendByDefault = swimlane.isUserGoal || swimlane.type === "recommended";

  const SwimlaneImage = () =>
    swimlane.translationKey === "swimlanes.klim" ? (
      <div className="icon-container klim">
        <Svg svgName="logos/klim_logo_brandmark" className="logo" size="1.75rem" />
      </div>
    ) : (
      <div className="icon-container">
        <Icon iconName={swimlane.icon} size="m" />
      </div>
    );

  return (
    <ErrorBoundary>
      <section className="swimlane padded-small">
        <Row className="header-row">
          {/* prelint-ignore-col */}
          {appType.desktop ? (
            <>
              <Col sm={24} md={20} lg={16} className="header-container">
                <SwimlaneImage />
                <div className="header">
                  {swimlane.isUserGoal ? (
                    <>
                      <h2>{t("swimlanes.yourGoal")}</h2>
                      <span className="goal-explainer">{t(swimlane.translationKey)}</span>
                    </>
                  ) : (
                    <h2>{t(swimlane.translationKey)}</h2>
                  )}
                </div>
              </Col>
              <SwimlaneControls handleScroll={handleScroll} items={swimlane.articles.length} position={scrollPosition} visibleItems={3} />
            </>
          ) : (
            <Col className="header-container">
              <SwimlaneImage />
              <div className="header">
                {swimlane.isUserGoal ? (
                  <>
                    <h2>{t("swimlanes.yourGoal")}</h2>
                    <span className="goal-explainer">{t(swimlane.translationKey)}</span>
                  </>
                ) : (
                  <h2>{t(swimlane.translationKey)}</h2>
                )}
              </div>
            </Col>
          )}
        </Row>
        <Row>
          <Col>
            <Carousel {...carouselProps(appType, ref, 3)}>
              {swimlane.articles.filter(filterDairyArticles).map((article) => (
                <ArticleCard hideDate key={article.id} link={article.link} recommendByDefault={recommendByDefault} />
              ))}
            </Carousel>
          </Col>
        </Row>
      </section>
    </ErrorBoundary>
  );
};

const ModuleSwimlane = ({ appType, filterDairyArticles, swimlane, t, user }) => {
  const { handleScroll, ref, scrollPosition } = useScroll();

  const recommendByDefault = swimlane.isActiveModule;

  const moduleExplainer = swimlane.isActiveModule ? (
    <span className="green-text method-explainer">{t("swimlanes.activeModule")}</span>
  ) : (
    <span className="grey-text method-explainer">{t("swimlanes.otherModule")}</span>
  );

  if (swimlane.moduleSlug === "dairy" && !user?.moduleAccess === "ALL") {
    return null;
  }

  return (
    <ErrorBoundary>
      <section className="swimlane padded-small">
        <Row className="header-row">
          {/* prelint-ignore-col */}
          {appType.desktop ? (
            <>
              <Col sm={24} md={20} lg={16} className="header-container">
                <div className="icon-container">
                  <Icon iconName={swimlane.icon} size="m" />
                </div>
                <div className="header">
                  {moduleExplainer}
                  <h2>{t(`modules:modules.${swimlane.moduleSlug}.name`)}</h2>
                </div>
              </Col>
              <SwimlaneControls handleScroll={handleScroll} items={swimlane.articles.length} position={scrollPosition} visibleItems={3} />
            </>
          ) : (
            <Col className="header-container">
              <div className="icon-container">
                <Icon iconName={swimlane.icon} size="m" />
              </div>
              <div className="header">
                {moduleExplainer}
                <h2>{t(`modules:modules.${swimlane.moduleSlug}.name`)}</h2>
              </div>
            </Col>
          )}
        </Row>
        <Row>
          <Col>
            <Carousel {...carouselProps(appType, ref, 3)}>
              {swimlane.articles.filter(filterDairyArticles).map((article) => (
                <ArticleCard hideDate key={article.id} link={article.link} recommendByDefault={recommendByDefault} />
              ))}
            </Carousel>
          </Col>
        </Row>
      </section>
    </ErrorBoundary>
  );
};

const NewsSwimlane = ({ appType, filterDairyArticles, swimlane, t }) => {
  const { handleScroll, ref, scrollPosition } = useScroll(appType);

  return (
    <ErrorBoundary>
      <section className="swimlane padded-small">
        <Row className="header-row">
          {/* prelint-ignore-col */}
          {appType.desktop ? (
            <>
              <Col sm={24} md={20} lg={16} className="header-container">
                <div className="icon-container">
                  <Icon iconName={swimlane.icon} size="m" />
                </div>
                <h2>{t(swimlane.translationKey)}</h2>
              </Col>
              <SwimlaneControls handleScroll={handleScroll} items={swimlane.articles.length} position={scrollPosition} visibleItems={4} />
            </>
          ) : (
            <Col className="header-container">
              <div className="icon-container">
                <Icon iconName={swimlane.icon} size="m" />
              </div>
              <h2>{t(swimlane.translationKey)}</h2>
            </Col>
          )}
        </Row>
        <Row>
          <Col>
            <Carousel {...carouselProps(appType, ref, 4)}>
              {swimlane.articles.filter(filterDairyArticles).map((article) => (
                <NewsCard height="400px" link={article.link} key={article.id} />
              ))}
            </Carousel>
          </Col>
        </Row>
      </section>
    </ErrorBoundary>
  );
};

export const Swimlane = ({ courses, swimlane }) => {
  const { t } = useTranslation(["search", "modules"]);
  const { user } = useUserContext();
  const appType = useAppType();

  const filterDairyArticles = (article) => {
    if (!article.tags.includes("dairy")) {
      return true;
    }
    return user?.moduleAccess === "ALL";
  };

  switch (swimlane.type) {
    case "goal":
    case "category":
    case "recommended":
      return swimlane.articles.length ? <GoalSwimlane appType={appType} filterDairyArticles={filterDairyArticles} swimlane={swimlane} t={t} /> : null;
    case "courses":
      return courses.length ? <CourseSwimlane appType={appType} courses={courses} swimlane={swimlane} t={t} /> : null;
    case "module":
      return swimlane.articles.length ? (
        <ModuleSwimlane appType={appType} filterDairyArticles={filterDairyArticles} swimlane={swimlane} t={t} user={user} />
      ) : null;
    case "news":
      return swimlane.articles.length ? <NewsSwimlane appType={appType} filterDairyArticles={filterDairyArticles} swimlane={swimlane} t={t} /> : null;
    default:
      return null;
  }
};
